import React, { useState, useEffect } from 'react';
import './Etape4.css';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';

function Etape4({ mission, onBack, onSave }) {
  // Documents classiques
  const documentsClassiques = [
    {
      id: "plan_locaux_risques",
      libelle: "Plan des locaux avec indication des locaux à risques particuliers d'influences externes, particulièrement BE2 et BE3",
      commentaires_predefined: [
        "Classement des locaux proposé par le vérificateur selon le guide UTE C15-103",
        "Classement des zones à risques d'explosion (BE3) fourni par le chef d'établissement",
        "Absence de classement ou classement non à jour des risques d'explosion (BE3)"
      ]
    },
    {
      id: "plan_masse_terre",
      libelle: "Plan de masse à l'échelle avec implantation des prises de terre et canalisations enterrées"
    },
    {
      id: "cahier_prescriptions",
      libelle: "Cahier des prescriptions techniques ayant permis la réalisation des installations"
    },
    {
      id: "schemas_unifilaires",
      libelle: "Schémas unifilaires des installations électriques, accompagnés si nécessaire du synoptique",
      commentaires_predefined: [
        "Etablis par le vérificateur",
        "Liste des circuits réalisés conformément aux prescriptions",
        "Un schéma unifilaire à jour doit être joint au dossier technique"
      ]
    },
    {
      id: "notes_calcul",
      libelle: "Notes de calcul justifiant des dimensionnement des canalisations et des dispositifs de protection"
    },
    {
      id: "carnets_cables",
      libelle: "Carnets de câbles"
    },
    {
      id: "rapports_verification",
      libelle: "Rapport de vérification initiale et rapports de vérifications périodiques postérieures",
      commentaires_predefined: [
        "Voir référence dans le rapport",
        "L'Inspection a été menée comme une vérification initiale"
      ]
    },
    {
      id: "declaration_ce",
      libelle: "Déclaration CE de conformité et notices d'instruction du matériel installé dans les locaux ou emplacements à risque d'explosion",
      commentaires_predefined: [
        "Marquage approprié à la zone ATEX indiqué sur le(s) matériel(s)",
        "Validation de conformité du matériel installé dans la zone à risque d'explosion",
        "L'absence de déclaration de conformité du matériel installé en zone à risque d'explosion est signalée dans le rapport"
      ]
    },
    {
      id: "liste_securite",
      libelle: "Liste des installations de sécurité et effectif maximal des différents locaux ou bâtiments",
      commentaires_predefined: [
        "Transmis oralement par le chef d'établissement",
        "Indiqués dans le registre de sécurité"
      ]
    },
    {
      id: "attestations_conformite",
      libelle: "Copie des attestations de conformité établies en application du décret 72-1120 du 14 décembre 1972"
    }
  ];

  // Documents RVRAT
  const documentsRVRAT = [
    {
      id: "schemas_unifilaires",
      libelle: "Schémas unifilaires et de distribution des installations électriques modifiées"
    },
    {
      id: "plans_architecte",
      libelle: "Plans architecte avec localisation des installations électriques"
    },
    {
      id: "notices_techniques",
      libelle: "Notices techniques des équipements installés"
    },
    {
      id: "attestations_conformite_materiels",
      libelle: "Attestations de conformité des matériels installés"
    },
    {
      id: "rapports_bureau_controle",
      libelle: "Rapports du bureau de contrôle technique (si applicable)"
    },
    {
      id: "dossier_technique",
      libelle: "Dossier technique amiante (DTA) ou repérage amiante"
    },
    {
      id: "autorisation_travaux",
      libelle: "Autorisation de travaux délivrée par la commission de sécurité"
    },
    {
      id: "proces_verbal_reception",
      libelle: "Procès-verbal de réception des travaux"
    },
    {
      id: "dernier_rapport_verification",
      libelle: "Dernier rapport de vérification périodique de l'établissement"
    }
  ];

  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Détecter le type de rapport depuis la mission (simulé ici)
  const isRVRAT = mission.type_verification === "Vérification après travaux ERP 1er groupe (RVRAT)";
  const documentsList = isRVRAT ? documentsRVRAT : documentsClassiques;

  useEffect(() => {
    loadSavedData();
  }, []);

  const loadSavedData = async () => {
    try {
      const saved = await inspectionStorage.getStep(mission.id, 'etape_4_documents');
      if (saved) {
        setFormData(saved);
        console.log('📦 Données Étape 4 récupérées depuis le cache');
      } else {
        // Initialiser avec "non_presente" par défaut
        const initialData = {};
        documentsList.forEach(doc => {
          initialData[doc.id] = {
            statut: 'non_presente',
            commentaire: ''
          };
        });
        setFormData(initialData);
      }
    } catch (error) {
      console.error('❌ Erreur chargement cache:', error);
    }
  };

  const updateDocumentStatut = (docId, statut) => {
    setFormData(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        statut: statut
      }
    }));
  };

  const updateDocumentCommentaire = (docId, commentaire) => {
    setFormData(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        commentaire: commentaire
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const stepData = {
        ...formData,
        timestamp: new Date().toISOString()
      };
      
      // 1. Sauvegarder dans IndexedDB
      await inspectionStorage.saveStep(mission.id, 'etape_4_documents', stepData);
      
      // 2. 🔥 AUTO-SYNC vers Base44
      syncManager.syncStep(mission.id, 'etape_4_documents', stepData);
      
      console.log('✅ Étape 4 sauvegardée localement');
      
      if (onSave) {
        onSave(formData);
      }

      alert('✅ Étape 4 sauvegardée avec succès !');
      
      onBack();
      
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="etape-container">
      {/* Header */}
      <div className="etape-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <div className="etape-title">
          <h1>Étape 4/14</h1>
          <p>{isRVRAT ? 'Documents présentés (RVRAT)' : 'Documents présentés'}</p>
        </div>
        <div></div>
      </div>

      {/* Content */}
      <div className="etape-content">
        
        {/* Info */}
        <div className={`alert ${isRVRAT ? 'alert-purple' : 'alert-info'}`}>
          ℹ️ {isRVRAT 
            ? "Indiquez les documents relatifs aux travaux réalisés qui vous ont été présentés lors de la vérification."
            : "Indiquez les documents qui vous ont été présentés lors de la vérification."
          }
        </div>

        {/* Tableau des documents */}
        <div className="documents-table-container">
          <table className="documents-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Statut</th>
                <th>Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {documentsList.map((doc, index) => {
                const docData = formData[doc.id] || { statut: 'non_presente', commentaire: '' };
                
                return (
                  <tr key={doc.id} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td className="doc-libelle">{doc.libelle}</td>
                    
                    <td className="doc-statut">
                      <div className="radio-group">
                        <label className="radio-item">
                          <input
                            type="radio"
                            name={`statut-${doc.id}`}
                            value="presente"
                            checked={docData.statut === 'presente'}
                            onChange={(e) => updateDocumentStatut(doc.id, e.target.value)}
                          />
                          <span>Présenté</span>
                        </label>
                        
                        <label className="radio-item">
                          <input
                            type="radio"
                            name={`statut-${doc.id}`}
                            value="non_presente"
                            checked={docData.statut === 'non_presente'}
                            onChange={(e) => updateDocumentStatut(doc.id, e.target.value)}
                          />
                          <span>Non présenté</span>
                        </label>
                        
                        <label className="radio-item">
                          <input
                            type="radio"
                            name={`statut-${doc.id}`}
                            value="sans_objet"
                            checked={docData.statut === 'sans_objet'}
                            onChange={(e) => updateDocumentStatut(doc.id, e.target.value)}
                          />
                          <span>Sans objet</span>
                        </label>
                      </div>
                    </td>
                    
                    <td className="doc-commentaire">
                      {doc.commentaires_predefined ? (
                        <div className="commentaire-group">
                          <select
                            value={doc.commentaires_predefined.includes(docData.commentaire) ? docData.commentaire : ''}
                            onChange={(e) => updateDocumentCommentaire(doc.id, e.target.value)}
                            className="form-select"
                          >
                            <option value="">Sélectionner un commentaire...</option>
                            {doc.commentaires_predefined.map((comment, idx) => (
                              <option key={idx} value={comment}>{comment}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={!doc.commentaires_predefined.includes(docData.commentaire) ? docData.commentaire : ''}
                            onChange={(e) => updateDocumentCommentaire(doc.id, e.target.value)}
                            placeholder="Ou saisir un texte libre..."
                            className="form-input"
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={docData.commentaire}
                          onChange={(e) => updateDocumentCommentaire(doc.id, e.target.value)}
                          placeholder="Commentaire..."
                          className="form-input"
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="etape-actions">
        <button 
          className="btn btn-secondary" 
          onClick={onBack}
          disabled={isSaving}
        >
          Annuler
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? '💾 Sauvegarde...' : '💾 Sauvegarder et continuer'}
        </button>
      </div>
    </div>
  );
}

export default Etape4;